import { AUTH_TYPES, TEMPLATE_CATEGORIES } from './constants.js'

/*
 * ServiceNow incident notifier for privacy request events (Table API).
 *
 * POSTs to /api/now/table/incident on the tenant's ServiceNow instance. Auth follows the framework's
 * single-auth-per-system constraint: the no-auth type is selected and the admin pastes a static
 * Authorization: Basic <base64(user:password)> custom header (ServiceNow native OAuth2 client
 * credentials is an alternative the admin can select at system level; the static-header route is the
 * default for setup simplicity). urgency/impact use ServiceNow's 1 (high) - 3 (low) scale.
 */
export const servicenowTemplate = {
	id: 'servicenow',
	guid: '382c04d2-fab6-446b-875c-2bd926136461',
	name: 'ServiceNow',
	category: TEMPLATE_CATEGORIES.SUPPORT,
	popularity: 'Enterprise',
	color: '#81b5a1',
	icon: 'mdi-cog-outline',
	description: 'Create ServiceNow incidents when privacy requests are raised or approach and breach their statutory deadlines.',
	features: ['Incident Management', 'Deadline Escalation', 'Urgency/Impact Mapping', 'ITSM'],
	endpoints: [
		{
			name: 'Create Incident - New Privacy Request',
			url: 'https://[instance].service-now.com/api/now/table/incident',
			method: 'POST',
			description: 'Create a ServiceNow incident when a new privacy request is submitted. Replace [instance] with your ServiceNow instance name.',
			cassieObjectID: -13,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'RequestTypeName', filter: '' },
				{ name: 'StatusName', filter: '' },
				{ name: 'SubjectEmail', filter: '' }
			],
			requestBody: '{ "short_description": "New Privacy Request [PLACEHOLDER_REQUESTNUMBER]", "description": "Privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]) of type [PLACEHOLDER_REQUESTTYPENAME] was submitted. Current status: [PLACEHOLDER_STATUSNAME]. Data subject: [PLACEHOLDER_SUBJECTEMAIL].", "urgency": "3", "impact": "3" }',
			headers: [
				{ Name: 'Authorization', value: 'Basic [BASE64_USER_PASSWORD]' },
				{ Name: 'Content-Type', value: 'application/json' },
				{ Name: 'Accept', value: 'application/json' }
			],
			successResponses: [{ code: 201, message: '' }]
		},
		{
			name: 'Create Incident - Request/Task Nearing Deadline',
			url: 'https://[instance].service-now.com/api/now/table/incident',
			method: 'POST',
			description: 'Create a ServiceNow incident when a privacy request or task enters its warning window.',
			cassieObjectID: -16,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'Deadline', filter: '' },
				{ name: 'DaysUntilDue', filter: '' }
			],
			requestBody: '{ "short_description": "[PLACEHOLDER_EVENTTITLE] - Request [PLACEHOLDER_REQUESTNUMBER]", "description": "[PLACEHOLDER_EVENTTITLE] for privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]). Deadline [PLACEHOLDER_DEADLINE]; days until due: [PLACEHOLDER_DAYSUNTILDUE].", "urgency": "2", "impact": "2" }',
			headers: [
				{ Name: 'Authorization', value: 'Basic [BASE64_USER_PASSWORD]' },
				{ Name: 'Content-Type', value: 'application/json' },
				{ Name: 'Accept', value: 'application/json' }
			],
			successResponses: [{ code: 201, message: '' }]
		},
		{
			name: 'Create Incident - Deadline Breached',
			url: 'https://[instance].service-now.com/api/now/table/incident',
			method: 'POST',
			description: 'Create a high-urgency ServiceNow incident when a privacy request or task passes its statutory deadline.',
			cassieObjectID: -17,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'Deadline', filter: '' },
				{ name: 'DaysOverdue', filter: '' }
			],
			requestBody: '{ "short_description": "[PLACEHOLDER_EVENTTITLE] - Request [PLACEHOLDER_REQUESTNUMBER]", "description": "[PLACEHOLDER_EVENTTITLE] for privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]). Deadline was [PLACEHOLDER_DEADLINE]; days overdue: [PLACEHOLDER_DAYSOVERDUE].", "urgency": "1", "impact": "1" }',
			headers: [
				{ Name: 'Authorization', value: 'Basic [BASE64_USER_PASSWORD]' },
				{ Name: 'Content-Type', value: 'application/json' },
				{ Name: 'Accept', value: 'application/json' }
			],
			successResponses: [{ code: 201, message: '' }]
		}
	],
	defaults: {
		endpointAddress: 'https://[instance].service-now.com',
		authenticationTypeId: AUTH_TYPES.NO_AUTH,
		requestThrottlingWaitTimeMS: 1000,
		notes: 'ServiceNow incidents for privacy request events via the Table API. No runtime auth is attached: replace [instance] and the Authorization header with Basic base64(user:password), treated as a revocable credential (native OAuth2 client credentials is an alternative selectable at system level). urgency/impact use ServiceNow 1 (high) to 3 (low). Keep a non-zero throttle; the first sweep after deploy may burst a backlog of breached requests.'
	}
}
